// File: frontend/src/components/registration/LoginModal.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Modal.css";
import { InputClearButton } from "../common/InputClearButton";
import { useTranslation } from "react-i18next";
import { useAuth, LoginOutcome } from "../../context/AuthContext";
import { parseApiError } from "../../utils/parseApiError";
import { cancelAccountDeletion } from "../../services/accountDeletionService";
import { resendVerificationEmail } from "../../services/emailService";
import { parseServerDate } from "../../utils/dateUtils";

// Minimal typing for the Google Identity Services script (loaded dynamically below).
// Only the pieces we actually use are declared — the full SDK surface is much larger.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: string;
              shape?: string;
              theme?: string;
              size?: string;
              text?: string;
              width?: string | number;
              locale?: string;
            }
          ) => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const GOOGLE_GSI_SCRIPT_ID = "google-identity-services-script";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister?: () => void;
  onForgotPassword?: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onForgotPassword,
}) => {
  const { t, i18n } = useTranslation();
  const { login, loginWithGoogle, updateUserStatus } = useAuth();

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  // Separate loading flag for the Google flow, since it is triggered by Google's
  // own button rather than our submit button.
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonContainerRef = useRef<HTMLDivElement>(null);

  // ── Pending self-deletion notice ──
  // A user whose 24-hour lock has expired is let in on purpose: logging in is how
  // they cancel. Closing the modal silently would hand them a normal-looking session
  // and let the deletion date pass unnoticed, so the login form turns into a warning
  // instead. Holds the deletion date while that warning is on screen; null otherwise.
  const [deletionNotice, setDeletionNotice] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // ── Unverified email ──
  // A correct password on an account that never clicked the verification link is
  // refused with EMAIL_NOT_VERIFIED. If the 24-hour link has since expired that is
  // a dead end: the old mail no longer works, the address is taken so registering
  // again fails, and nothing on screen offers a way out. These three flags drive
  // the "send me a new link" button that appears under that specific error.
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendInfo, setResendInfo] = useState<string | null>(null);

  // ── Banned account ──
  // A ban is the one refusal with no path forward on this screen: there is no
  // expiry to wait out, no link to resend and no field to correct. Without a way
  // to reach a human, a wrongly banned user simply has nowhere to go — so the
  // same "extra offer under the error" treatment the resend button gets is used
  // to point them at the public contact page, where the moderation e-mail the
  // admin configured is already published.
  const [showBanAppeal, setShowBanAppeal] = useState(false);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        identifier: "",
        password: "",
      });
      setErrors({});
      setDeletionNotice(null);
      setCancelError(null);
      setShowResend(false);
      setResendInfo(null);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Accept both email addresses and usernames — no format restriction
    if (!formData.identifier.trim()) {
      newErrors.identifier = t("login_error_identifier_required");
    }

    if (!formData.password) {
      newErrors.password = t("login_error_password_required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Empty a field in one click. Mirrors handleChange so the same error-clearing
   * rules apply, then puts focus back in the input — clearing a field the user
   * is about to retype should not cost them a click to get back in.
   */
  const clearField = (name: "identifier" | "password") => {
    setFormData((prev) => ({ ...prev, [name]: "" }));
    setErrors((prev) => ({ ...prev, [name]: "", general: "" }));
    document.getElementById(`login-${name}`)?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    
    // Clear general error when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: "" }));
    }

    // Editing the identifier means the resend offer no longer refers to the account
    // it was raised for — sending a link to whatever is half-typed in the box would
    // be worse than useless. Password edits leave it alone: same account, and the
    // user may well want the link after a failed second attempt.
    if (name === "identifier") {
      setShowResend(false);
      setResendInfo(null);
    }
  };

  // Use shared parseApiError util for consistent error handling

  /**
   * Decide what happens after the session has been established.
   * Returns true when the modal handled the outcome by showing the deletion warning,
   * in which case the caller must NOT close the modal.
   */
  const handleLoginOutcome = (outcome: LoginOutcome): boolean => {
    if (outcome.status === "DELETED_BY_USER" && outcome.scheduledDeletionAt) {
      setDeletionNotice(outcome.scheduledDeletionAt);
      return true;
    }
    return false;
  };

  /** Cancel the pending deletion straight from the warning, without leaving the modal. */
  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      await cancelAccountDeletion();
      // The backend restores ACTIVE; mirror it locally so the header and profile
      // stop showing the deletion state without forcing a re-login.
      updateUserStatus("ACTIVE");
      setDeletionNotice(null);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      setCancelError(parseApiError(message, t));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({}); // Clear previous errors
    setResendInfo(null);
    
    try {
      const outcome = await login(formData.identifier, formData.password);
      if (!handleLoginOutcome(outcome)) {
        onClose();
      }
    } catch (error) {
      console.error("Login failed:", error);
      
      const errorMessage = error instanceof Error ? error.message : t("login_error_general");
      // Read the raw code before parseApiError turns it into a sentence: the resend
      // button must appear for this one rejection and no other.
      setShowResend(errorMessage === "EMAIL_NOT_VERIFIED");
      // Same reasoning, read from the raw code as well: a ban arrives either as the
      // bare code or as "ACCOUNT_BANNED:<reason>", and both need the contact link.
      setShowBanAppeal(errorMessage.startsWith("ACCOUNT_BANNED"));
      const parsedError = parseApiError(errorMessage, t);
      setErrors({ general: parsedError });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Request a fresh verification link for the account that was just refused.
   *
   * The identifier already in the form is enough for the backend to find the
   * account, so the user does not have to retype their address. The response is
   * deliberately uninformative (the endpoint answers 200 even for an unknown
   * account, to avoid confirming which addresses exist), so the confirmation is
   * worded as "if that account is waiting for a link, one is on its way".
   */
  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendInfo(null);
    try {
      const language = i18n.language?.startsWith("tr") ? "tr" : "en";
      await resendVerificationEmail({ identifier: formData.identifier.trim() }, language);
      setResendInfo(t("verify_resend_sent"));
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      // parseApiError translates the cooldown code; anything else falls back to
      // the generic resend failure rather than the login wording.
      setResendInfo(parseApiError(message, t, "verify_resend_error"));
    } finally {
      setResendLoading(false);
    }
  };

  // Called by Google's script once the user picks an account and Google issues a credential.
  // Sends the ID token to our backend, which verifies it and returns a normal session.
  const handleGoogleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      setIsGoogleLoading(true);
      setErrors({});
      try {
        const outcome = await loginWithGoogle(response.credential);
        if (outcome.status === "DELETED_BY_USER" && outcome.scheduledDeletionAt) {
          setDeletionNotice(outcome.scheduledDeletionAt);
        } else {
          onClose();
        }
      } catch (error) {
        console.error("Google login failed:", error);
        const errorMessage = error instanceof Error ? error.message : t("login_error_google_failed");
        // A banned account is refused on this path exactly as on the password one,
        // so the way out has to be offered here too.
        setShowBanAppeal(errorMessage.startsWith("ACCOUNT_BANNED"));
        const parsedError = parseApiError(errorMessage, t);
        setErrors({ general: parsedError });
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [loginWithGoogle, onClose, t]
  );

  // Load the Google Identity Services script once (shared across modal open/close cycles),
  // then render the Google button into our container whenever the modal opens.
  useEffect(() => {
    if (!isOpen || !GOOGLE_CLIENT_ID) return;

    const renderGoogleButton = () => {
      if (!window.google || !googleButtonContainerRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
      });
      // Clear any previously rendered button before re-rendering (e.g. modal reopened)
      googleButtonContainerRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleButtonContainerRef.current, {
        type: "icon",
        shape: "circle",
        size: "large",
        locale: i18n.language?.startsWith("tr") ? "tr" : "en",
      });
    };

    const existingScript = document.getElementById(GOOGLE_GSI_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      // Script already loaded (or loading) from a previous modal open
      if (window.google) {
        renderGoogleButton();
      } else {
        existingScript.addEventListener("load", renderGoogleButton, { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_GSI_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", renderGoogleButton, { once: true });
    document.body.appendChild(script);
  }, [isOpen, i18n.language, handleGoogleCredentialResponse]);

  if (!isOpen) return null;

  // ── Pending-deletion warning ──
  // Replaces the form entirely: the user is already signed in at this point, and the
  // only thing that matters is that they see the date and know how to stop it.
  if (deletionNotice) {
    const scheduled = parseServerDate(deletionNotice);
    const scheduledLabel = isNaN(scheduled.getTime())
      ? ""
      : scheduled.toLocaleString(i18n.language?.startsWith("tr") ? "tr-TR" : "en-US", {
          day: "numeric", month: "long", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });

    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="modal-header">
            <h2>{t("login_deletion_notice_title")}</h2>
          </div>

          <div className="modal-form">
            <div className="error-message general-error">
              {t("login_deletion_notice_body", { date: scheduledLabel })}
            </div>
            <p className="deletion-notice-hint">{t("login_deletion_notice_hint")}</p>

            {cancelError && <div className="error-message general-error">{cancelError}</div>}

            <button
              type="button"
              className="submit-btn"
              onClick={handleCancelDeletion}
              disabled={cancelLoading}
            >
              {cancelLoading ? t("account_delete_loading") : t("login_deletion_notice_cancel")}
            </button>

            <button
              type="button"
              className="link-btn"
              style={{ display: "block", margin: "var(--spacing-sm) auto 0" }}
              onClick={onClose}
              disabled={cancelLoading}
            >
              {t("login_deletion_notice_continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-container">
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="modal-header">
          <h2>{t("login_title")}</h2>
        </div>

        <form onSubmit={handleSubmit} className="modal-form" noValidate>
          {errors.general && (
            <div className="error-message general-error">{errors.general}</div>
          )}

          {/* Way out of the expired-link dead end — only rendered for EMAIL_NOT_VERIFIED */}
          {showResend && (
            <div className="login-resend-block">
              <button
                type="button"
                className="link-btn"
                onClick={handleResendVerification}
                disabled={resendLoading || isLoading || isGoogleLoading}
              >
                {resendLoading ? t("verify_resend_loading") : t("verify_resend_button")}
              </button>
              {resendInfo && <p className="login-resend-info">{resendInfo}</p>}
            </div>
          )}

          {/* Way out of the ban dead end — only rendered for ACCOUNT_BANNED.
              Points at the public contact page rather than hardcoding an address:
              the moderation e-mail lives in system settings and the admin can
              change it without a redeploy. */}
          {showBanAppeal && (
            <div className="login-ban-appeal">
              <p className="login-ban-appeal__text">{t("login_banned_appeal_text")}</p>
              <Link to="/iletisim" className="link-btn" onClick={onClose}>
                {t("login_banned_appeal_link")}
              </Link>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="login-identifier">{t("login_identifier_label")}</label>
            <div className="input-with-clear">
              <input
                type="text"
                id="login-identifier"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                className={errors.identifier ? "input-error" : ""}
                placeholder={t("login_identifier_placeholder")}
                disabled={isLoading || isGoogleLoading}
                autoComplete="username"
              />
              {formData.identifier && (
                <InputClearButton onClear={() => clearField("identifier")} />
              )}
            </div>
            {errors.identifier && (
              <span className="field-error">{errors.identifier}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="login-password">{t("login_password_label")}</label>
            <div className="input-with-clear">
              <input
                type="password"
                id="login-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? "input-error" : ""}
                placeholder={t("login_password_placeholder")}
                disabled={isLoading || isGoogleLoading}
              />
              {formData.password && (
                <InputClearButton onClear={() => clearField("password")} />
              )}
            </div>
            {errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
          </div>

          {/* Forgot password link — shown below password input */}
          <div style={{ textAlign: "right", marginTop: "-8px", marginBottom: "8px" }}>
            <button
              type="button"
              className="link-btn"
              style={{ fontSize: "var(--font-size-sm)" }}
              onClick={onForgotPassword}
              disabled={isLoading || isGoogleLoading}
            >
              Şifremi Unuttum
            </button>
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading || isGoogleLoading}>
            {isLoading ? t("login_button_loading") : t("login_button")}
          </button>

          {/* Social login row — icon buttons for alternative sign-in providers.
              Currently only Google; more providers (Apple, Facebook, etc.) can be
              added here later as additional icon buttons in the same row. */}
          {GOOGLE_CLIENT_ID && (
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "var(--spacing-xs)", marginBottom: "calc(-1 * (var(--spacing-2xl) - var(--spacing-xs)))" }}>
              <div
                ref={googleButtonContainerRef}
                title={t("login_google_button")}
                style={{
                  minWidth: "44px",
                  minHeight: "44px",
                  opacity: isGoogleLoading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </div>
          )}
        </form>

        <div className="modal-footer">
          <p>
            {t("login_no_account")}{" "}
            <button type="button" className="link-btn" onClick={onSwitchToRegister} disabled={isLoading || isGoogleLoading}>
              {t("login_register_link")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};